import React, { useEffect, useState } from 'react';
import { Input, InputProps } from '@indigo-ag/component-module';

export interface InputNumberMaskProps {
  onNumberChange: (num: number | null) => void;
  numberValue?: number;
  reset?: boolean;
  nullable?: boolean;
  allowNegative?: boolean;
}

export const InputNumberMask: React.FunctionComponent<InputNumberMaskProps & InputProps> = ({
  onNumberChange,
  label,
  numberValue,
  reset = false,
  nullable = false,
  allowNegative = false,
  ...rest
}) => {
  /**
   * @param {number} value number to evaluate if is positive or negative
   */
  const isNegativeNumber = (value: number = 0) => !Object.is(Math.abs(value), +value);

  /**
   * @param {number} value Number where internationalization format is applied
   */
  const formatNumber = (value?: number): string => {
    if (nullable && (value === null || value === undefined)) {
      return '';
    } else {
      const isNegative = isNegativeNumber(value);
      const transformedValue = new Intl.NumberFormat(window.navigator.language, {
        minimumFractionDigits: 2
      }).format(value || 0);
      const shouldAddNegativeSign = isNegative && !transformedValue.startsWith('-');
      return shouldAddNegativeSign ? `-${transformedValue}` : transformedValue;
    }
  };

  /**
   * State
   */
  const [inputMaskedValue, setInputMaskedValue] = useState(formatNumber(numberValue));

  useEffect(() => {
    setInputMaskedValue(formatNumber(numberValue));
  }, [reset, numberValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const maskInputValue = (event: React.ChangeEvent<HTMLInputElement>): string => {
    const value = getNumberValue(event.target.value);
    const maskedValue = formatNumber(value);
    return maskedValue;
  };

  /**
   * @param {string} value Shaped value to be transformed as a number with two decimal points
   */
  const getNumberValue = (value: string): number | undefined => {
    if (allowNegative) {
      value = preventNegativeSignFromOverride(value);
    }
    const numberValue = parseFloat(value.replace(/[^0-9-]/g, ''));
    const result = numberValue / getPrecisionValue(2);
    onNumberChange(nullable && Number.isNaN(result) ? null : result);

    return Number.isNaN(result) ? undefined : result;
  };

  const preventNegativeSignFromOverride = (value: string): string => {
    const isJustNegativeSign = value.startsWith('-') && value.length === 1;
    return isJustNegativeSign ? '-0' : value;
  };

  /**
   * @param {number} decimals Number of point decimals that will be used on the expression
   */
  const getPrecisionValue = (decimals: number): number => Math.pow(10, decimals);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputMaskedValue(maskInputValue(event));
  };

  return <Input label={label} value={inputMaskedValue} onChange={handleChange} {...rest} />;
};
